'use client';

import React, { type FC } from 'react';
import { Credits } from './Credits';
import { FaSteam } from './icons';
import { isCsShotUzHost } from '../runtime/host';
import { useHost } from '../runtime/RuntimeContext';
import { formatUsdPrice } from '../utils/formatters';
import { cn } from '../utils/cn';

// WHY: на узбекском домене (cs-shot.uz) цены Steam в долларах показывать нельзя — правило живёт здесь, а не в экранах
export function useSteamPriceAllowed(): boolean {
  return !isCsShotUzHost(useHost());
}

export function useShowSteamPrice(steamPrice: number | null | undefined): boolean {
  const allowed = useSteamPriceAllowed();
  return allowed && steamPrice != null;
}

export type ItemPriceVariant = 'inherit' | 'cardPrice' | 'steamPrice' | 'dropPrice';

const VARIANT_CLASSES: Record<ItemPriceVariant, string> = {
  inherit: '',
  cardPrice: 'text-sm font-semibold text-price',
  steamPrice: 'text-xs font-semibold text-white/85',
  dropPrice: 'text-sm font-semibold text-white',
};

interface ItemPriceProps {
  credits?: number | null;
  steamPrice?: number | null;
  creditsIconSize?: number | string;
  steamIconSize?: number | string;
  showSteamIcon?: boolean;
  compact?: boolean;
  variant?: ItemPriceVariant;
  className?: string;
}

// WHY: точка на 60% opacity — минимум, дающий 3:1 контраст на самом тёмном и самом светлом реальном фоне
export const ItemPrice: FC<ItemPriceProps> = ({
  credits,
  steamPrice,
  creditsIconSize,
  steamIconSize = 12,
  showSteamIcon = true,
  compact = false,
  variant = 'inherit',
  className,
}) => {
  const showSteamPrice = useShowSteamPrice(steamPrice);

  if (credits == null && !showSteamPrice) return null;

  return (
    <span className={cn('inline-flex items-center gap-1', VARIANT_CLASSES[variant], className)}>
      {credits != null && <Credits amount={credits} iconSize={creditsIconSize} compact={compact} />}
      {showSteamPrice && (
        <span className="flex items-center">
          {credits != null && <span aria-hidden className="me-1.5 opacity-60">·</span>}
          {showSteamIcon && <FaSteam size={steamIconSize} className="me-1 shrink-0" />}
          ${formatUsdPrice(steamPrice!, { whole: compact })}
        </span>
      )}
    </span>
  );
};
